import {useLocale, useMessages} from "i18n";
import {
	WELCOME_METER_DOTS, welcomeMeterDots, welcomeSpotsShown, welcomeUntil, type WelcomeBandOccasion, type WelcomePriceState,
} from "./welcomePrice";
import {Band, Dot, Eyebrow, Meta, Meter, Neutral, Rule, Sentence, Until} from "./WelcomePriceBand.styles";

/**
 * The coupon band across the top of the pricing sheet (ui#2441): the eyebrow, how many spots the run
 * has left once that is five or fewer ({@link welcomeSpotsShown}, kitecorp/transferify#536), and the day
 * and time the run ends in the reader's own zone. At zero left, and when the run
 * has ended or been withdrawn, it is one neutral line instead. Dumb view: the state comes from
 * {@link usePricingPage}.
 *
 * <b>THERE IS NO TICKING CLOCK, deliberately</b> (legal round on ui#2441). The end date is material
 * information a reader is entitled to (CTSI Guidance section 19) and it stays; a per-second countdown
 * on an offer that returns about three weeks later is the urgency device the CMA's Emma Sleep consent
 * order is about, whose line is 28 days between substantially similar promotions, and the owner chose
 * monthly runs over a 40-day cadence. Do not add one back, and do not add "last chance", "only",
 * "exclusive" or "won't be back" beside it.
 *
 * The dot meter is kept behind {@link WELCOME_METER_DOTS} (owner: hidden for now); the sentence carries
 * the meaning, so the dots are decoration and aria-hidden when they return.
 */
export function WelcomePriceBand({welcome, occasion}: {welcome: WelcomePriceState; occasion: WelcomeBandOccasion}) {
	const {welcome: copy} = useMessages().pricing;
	const {locale} = useLocale();
	if (welcome.kind === "soldOut") return <Neutral role="status">{copy.soldOut}</Neutral>;
	if (welcome.kind === "ended") return <Neutral role="status">{copy.ended}</Neutral>;
	if (welcome.kind !== "live") return null;
	const until = welcomeUntil(welcome.deadline, locale);
	const spotsShown = welcomeSpotsShown(welcome.spotsLeft);
	return (
		<Band data-testid="welcome-price-band">
			<Eyebrow>{occasion === "halloween" ? copy.halloweenEyebrow : copy.eyebrow}</Eyebrow>
			<Meta>
				{spotsShown !== null && (
					<>
						<Sentence>
							{copy.spotsLeft(spotsShown)}
							{WELCOME_METER_DOTS && (
								<Meter aria-hidden="true">
									{welcomeMeterDots(spotsShown).map((_, index) => <Dot key={index}/>)}
								</Meter>
							)}
						</Sentence>
						<Rule aria-hidden="true"/>
					</>
				)}
				<Until dateTime={new Date(welcome.deadline).toISOString()}>{copy.until(until.date, until.time)}</Until>
			</Meta>
		</Band>
	);
}
